/**
 * useLoRaRoomBridge
 *
 * Carries one room over the connected LoRa radio.
 *
 * MeshCore (proven on hardware): the room is a private MeshCore channel keyed
 * from the room secret. While the room is on the radio, linkRoomToRadio()
 * (src/lib/meshcore/room-radio-link.ts) does the work:
 *  - what this phone authors goes on air now ("Ana› text ·tag");
 *  - fresh messages from internet peers are offered to the gateway, which
 *    forwards them after a staggered delay unless the tag is heard on air
 *    first (another phone already carried it);
 *  - what the radio hears joins the room, unless its internet original is
 *    already here; a radio copy and its original never both stay.
 *
 * Meshtastic (unverified on hardware): locally authored text and POIs only,
 * as JSON payloads.
 *
 * Never on air, on either backend: Yjs state sync. It used to be broadcast on
 * every document update, which would flood a channel the whole community
 * mesh shares.
 *
 * ⚠️ EXPERIMENTAL: Meshtastic path unverified on hardware.
 */

import { useEffect, useCallback, useState } from 'react'
import type * as Y from 'yjs'
import {
  isLoRaConnected,
  getActiveBackend,
  sendLoRaMessage,
  setLoRaEventHandlers,
  generateLoRaMessageId,
  validateLoRaMessage,
} from '@/lib/lora'
import type { LoRaMessage, LoRaChannelMapping, LoRaBridgeOptions } from '@/lib/lora'
import { meshcoreSession, type RadioState } from '@/lib/meshcore'
import { linkRoomToRadio, LORA_ORIGIN } from '@/lib/meshcore/room-radio-link'
import type { MessageEntry, RoomDocument } from '@/lib/sync/crdt'

export interface UseLoRaRoomBridgeOptions extends LoRaBridgeOptions {
  roomDoc: RoomDocument | null
}

export interface UseLoRaRoomBridgeReturn {
  isActive: boolean
  /** Kept for API compatibility; MeshCore rooms map through the radio session. */
  channelMapping: LoRaChannelMapping | null
  broadcastRoomMessage: (messageData: Record<string, unknown>) => Promise<void>
  /** No-op: CRDT state never goes on air. */
  broadcastStateSync: (update: Uint8Array) => Promise<void>
}

export function useLoRaRoomBridge({
  roomId,
  roomSecret,
  enabled,
  roomDoc,
}: UseLoRaRoomBridgeOptions): UseLoRaRoomBridgeReturn {
  const [radio, setRadio] = useState<RadioState>(meshcoreSession.snapshot)
  const [joined, setJoined] = useState(false)
  useEffect(() => meshcoreSession.onState(setRadio), [])

  const backend = getActiveBackend()
  const meshcoreReady = enabled && backend === 'meshcore' && radio.status === 'ready'
  const isActive = enabled && isLoRaConnected() && roomDoc !== null
  const gatewayKey = radio.selfInfo?.publicKey ?? ''

  // ---- MeshCore: put the room's channel on the radio while the room is open ----
  useEffect(() => {
    if (!meshcoreReady || !roomSecret || !roomId) return
    let cancelled = false
    meshcoreSession
      .joinRoom(roomId, roomSecret)
      .then(() => !cancelled && setJoined(true))
      .catch((e) => console.error('[LoRaBridge] could not put room on radio:', e))
    return () => {
      cancelled = true
      setJoined(false)
      // Leaving the room takes its channel key off the radio (seizure hygiene).
      void meshcoreSession.leaveRoom(roomId)
    }
  }, [meshcoreReady, roomId, roomSecret])

  // ---- MeshCore: link the room to the radio (own messages, gateway, arrivals) ----
  useEffect(() => {
    if (!joined || !roomDoc || !gatewayKey) return
    return linkRoomToRadio({
      doc: roomDoc.doc,
      messages: roomDoc.messages,
      roomId,
      radio: meshcoreSession,
      gatewayKey,
    })
  }, [joined, roomDoc, roomId, gatewayKey])

  // ---- Meshtastic: room -> radio (locally authored text and POIs) ----
  useEffect(() => {
    if (!roomDoc || !enabled || backend !== 'meshtastic') return
    const observer = (event: Y.YArrayEvent<MessageEntry>, txn: Y.Transaction) => {
      if (!txn.local || txn.origin === LORA_ORIGIN) return
      event.changes.added.forEach((item) => {
        for (const entry of item.content.getContent() as MessageEntry[]) {
          if (entry.type !== 'text' && entry.type !== 'poi') continue
          if (entry.poiType === 'vehicle_sighting') continue
          const message: LoRaMessage = {
            id: generateLoRaMessageId(),
            type: entry.type,
            roomId,
            payload: JSON.stringify(entry),
            timestamp: Date.now(),
            backend: 'meshtastic',
          }
          sendLoRaMessage(message).catch((e) => console.error('[LoRaBridge] radio send failed:', e))
        }
      })
    }
    roomDoc.messages.observe(observer)
    return () => roomDoc.messages.unobserve(observer)
  }, [roomDoc, roomId, enabled, backend])

  // ---- Meshtastic: radio -> room (legacy JSON payloads) ----
  const handleMeshtasticMessage = useCallback(
    (message: LoRaMessage) => {
      if (!roomDoc || !enabled || message.roomId !== roomId) return
      if (!validateLoRaMessage(message)) return
      if (message.type !== 'text' && message.type !== 'poi') return
      try {
        const entry = JSON.parse(message.payload) as MessageEntry
        if (roomDoc.messages.toArray().some((m) => m.id === entry.id)) return
        roomDoc.doc.transact(() => roomDoc.messages.push([entry]), LORA_ORIGIN)
      } catch (e) {
        console.error('[LoRaBridge] bad Meshtastic payload:', e)
      }
    },
    [roomDoc, roomId, enabled]
  )

  useEffect(() => {
    if (!enabled || backend !== 'meshtastic') return
    setLoRaEventHandlers({ onMessageReceived: handleMeshtasticMessage })
    return () => setLoRaEventHandlers({})
  }, [enabled, backend, handleMeshtasticMessage])

  const broadcastRoomMessage = useCallback(
    async (messageData: Record<string, unknown>) => {
      if (!roomDoc) return
      // Adding to the room is enough: the link puts it on air.
      roomDoc.messages.push([messageData as unknown as MessageEntry])
    },
    [roomDoc]
  )

  const broadcastStateSync = useCallback(async () => undefined, [])

  return { isActive, channelMapping: null, broadcastRoomMessage, broadcastStateSync }
}
